import React, { useEffect, useState, useCallback } from 'react';
import { useTutorialStore } from '../stores/tutorialStore';
import { Book, ChevronDown, Heart, MessageSquare, Maximize2, Brain, ChevronRight } from 'lucide-react';
import '../styles/tutorial.css';

export const Tutorial: React.FC = () => {
  const { 
    isActive,
    currentStepIndex,
    getCurrentSteps,
    nextStep,
    previousStep,
    skipTutorial
  } = useTutorialStore();

  const [position, setPosition] = useState({ top: 0, left: 0 });
  const steps = getCurrentSteps();
  const currentStep = steps[currentStepIndex];

  // Example components for the tutorial
  const TutorialExamples = {
    'version-selector': (
      <div className="mb-4 p-2 bg-slate-800/50 rounded-md">
        <button className="w-full px-4 py-2 text-white bg-white/10 rounded-lg hover:bg-white/20 transition-colors flex items-center gap-2">
          <Book size={18} />
          <span>King James Version (KJV)</span>
          <ChevronDown size={16} />
        </button>
      </div>
    ),
    'navigation': (
      <div className="mb-4 p-2 bg-slate-800/50 rounded-md flex gap-2">
        <div className="relative flex-1">
          <button className="w-full px-4 py-2 text-white bg-white/10 rounded-lg hover:bg-white/20 transition-colors flex items-center justify-between">
            <span>Genesis</span>
            <ChevronDown size={16} />
          </button>
        </div>
        <div className="relative w-24">
          <button className="w-full px-4 py-2 text-white bg-white/10 rounded-lg hover:bg-white/20 transition-colors flex items-center justify-between">
            <span>1</span>
            <ChevronDown size={16} />
          </button>
        </div>
      </div>
    ),
    'verse-interaction': (
      <div className="mb-4 p-2 bg-slate-800/50 rounded-md">
        <div className="p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-all group">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <h3 className="text-gold-400 font-medium">
                Genesis 1:1
                <span className="text-sm text-white/40 ml-2">(KJV)</span>
              </h3>
            </div>
            <div className="flex items-center gap-2 animate-pulse">
              <button
                className="p-2 transition-colors rounded-lg bg-white/10 text-gold-400"
                title="AI Insights"
              >
                <Brain size={18} />
              </button>
              <button
                className="p-2 transition-colors rounded-lg bg-white/10 text-gold-400"
                title="Add note"
              >
                <MessageSquare size={18} />
              </button>
              <button
                className="p-2 transition-colors rounded-lg bg-white/10 text-gold-400"
                title="Add to favorites"
              >
                <Heart size={18} />
              </button>
            </div>
            <p className="text-white/80 font-serif text-lg leading-relaxed">
              In the beginning God created the heaven and the earth.
            </p>
          </div>
        </div>
      </div>
    ),
    'chapter-insights': (
      <div className="mb-4 p-2 bg-slate-800/50 rounded-md">
        <div className="flex items-center gap-2 px-2">
          <button className="p-2 text-white/40 hover:text-white transition-colors opacity-50">
            <Maximize2 size={16} />
          </button>
          <button className="p-2 text-gold-400 hover:text-gold-300 transition-colors animate-pulse">
            <Brain size={22} />
          </button>
        </div>
      </div>
    ),
    'verse-insights': (
      <div className="mb-4 p-2 bg-slate-800/50 rounded-md">
        <div className="flex flex-col gap-4 p-4 bg-slate-800 rounded-lg border border-white/10">
          <div className="flex items-center gap-2 text-gold-400">
            <Brain size={20} />
            <h3 className="text-lg font-medium">AI Verse Insights</h3>
          </div>
          <div className="flex gap-2">
            <button className="flex-1 px-4 py-2 text-center bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
              <span className="text-white/80">Brief Insights</span>
            </button>
            <button className="flex-1 px-4 py-2 text-center bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
              <span className="text-white/80">Detailed Insights</span>
            </button>
          </div>
        </div>
      </div>
    )
  };

  const calculatePosition = useCallback(() => {
    if (!currentStep?.element) {
      // Center in viewport if no element
      return {
        top: window.innerHeight / 2 - 100,
        left: window.innerWidth / 2 - 200,
      };
    }

    const element = document.querySelector(currentStep.element);
    if (!element) return null;

    const rect = element.getBoundingClientRect();
    const isMobile = window.innerWidth < 768;
    
    // Add highlight class to element
    element.classList.add('tutorial-highlight');

    // Calculate base position
    let top = rect.top;
    let left = rect.left;

    // Adjust position based on specified position and screen size
    switch (currentStep.position) {
      case 'top':
        top = isMobile ? rect.top + rect.height + 20 : rect.top - 220;
        left = isMobile ? 20 : rect.left;
        break;
      case 'bottom':
        top = rect.bottom + 20;
        left = isMobile ? 20 : rect.left;
        break;
      case 'left':
        top = isMobile ? rect.bottom + 20 : rect.top;
        left = isMobile ? 20 : (rect.left - 420);
        break;
      case 'right':
        top = isMobile ? rect.bottom + 20 : rect.top;
        left = isMobile ? 20 : (rect.right + 20);
        break;
      default:
        top = rect.bottom + 20;
        left = isMobile ? 20 : rect.left;
    }

    // Ensure tooltip stays within viewport
    const tooltipWidth = isMobile ? window.innerWidth - 40 : 400;
    const tooltipHeight = 200;

    // Adjust horizontal position
    if (left + tooltipWidth > window.innerWidth - 20) {
      left = window.innerWidth - tooltipWidth - 20;
    }
    if (left < 20) left = 20;

    // Adjust vertical position
    if (top + tooltipHeight > window.innerHeight - 20) {
      top = isMobile ? rect.top - tooltipHeight - 20 : rect.top - tooltipHeight - 20;
    }
    if (top < 20) top = 20;

    return { top, left };
  }, [currentStep]);

  useEffect(() => {
    const handleResize = () => {
      const position = calculatePosition();
      if (position) {
        setPosition(position);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculatePosition]);

  useEffect(() => {
    // Clean up previous highlight
    const prevHighlight = document.querySelector('.tutorial-highlight');
    if (prevHighlight) {
      prevHighlight.classList.remove('tutorial-highlight');
    }

    const position = calculatePosition();
    if (position) {
      setPosition(position);
    }

    return () => {
      const highlight = document.querySelector('.tutorial-highlight');
      if (highlight) {
        highlight.classList.remove('tutorial-highlight');
      }
    };
  }, [currentStep, calculatePosition]);

  useEffect(() => {
    if (isActive) {
      // Check for mobile device
      const isMobile = window.innerWidth < 768;
      
      // Force sidebar open on mobile when tutorial starts
      if (isMobile) {
        document.body.classList.add('tutorial-sidebar-open');
      }
    }
  }, [currentStep, isActive]);

  if (!isActive || !currentStep) return null;

  return (
    <>
      {/* Tutorial modal */}
      <div 
        className="fixed z-[1001] pointer-events-none"
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <div className="pointer-events-auto bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-xl p-6 w-[400px] max-w-[90vw] border border-white/10">
          <h3 className="text-xl font-semibold mb-2 text-white">{currentStep.title}</h3>
          <p className="text-white/80 mb-4 whitespace-pre-line">{currentStep.content}</p>
          
          {/* Example UI elements */}
          {currentStep.id in TutorialExamples && (
            <div className="mb-4">
              {TutorialExamples[currentStep.id as keyof typeof TutorialExamples]}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-6">
            <button
              className="px-4 py-2 text-white/60 hover:text-white disabled:opacity-50 transition-colors"
              onClick={previousStep}
              disabled={currentStepIndex === 0}
            >
              Previous
            </button>
            <div className="flex gap-4">
              <button
                className="px-4 py-2 text-white/60 hover:text-white transition-colors"
                onClick={skipTutorial}
              >
                Skip
              </button>
              <button
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                onClick={nextStep}
              >
                {currentStepIndex === steps.length - 1 ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
